<template>
    <Row v-if="downloadFormat === DOWNLOAD_FORMAT.DOUBLECLICK_SV">
        <Col span="16">
            <h4>Profile ID</h4>
            <p class="tooltip">
                Before downloading your ads, set up your campaign feed in <strong>Google Studio</strong> to get your
                <strong>Profile ID</strong> and add it here.
            </p>
            <p class="error">{{ dcProfileIdError }}</p>
        </Col>
        <Col span="8">
            <Input
                v-model="dcProfileId"
                placeholder="Google Studio Profile ID"
                data-testid="download-modal__dc-profile-id-input"
            />
        </Col>
    </Row>

    <Divider v-if="downloadFormat === DOWNLOAD_FORMAT.DOUBLECLICK_SV" size="small" />

    <DownloadOptionWrapper v-if="displayOptionsForFormat('ftClickTagCount')" tooltip="Flashtalking Click tag count">
        <Checkbox v-model="ftClickTagCount.enabled" data-testid="download-modal__click-tag-count-checkbox">
            <h4>Click Tag Count</h4>
        </Checkbox>

        <template #input>
            <Input
                v-model="ftClickTagCount.value"
                data-testid="download-modal__click-tag-count-input"
                class="small-input"
            />
        </template>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper
        v-if="displayOptionsForFormat('ftBrowserExclusions')"
        tooltip="Comma separated list of browsers"
    >
        <Checkbox v-model="ftBrowserExclusions.enabled" data-testid="download-modal__browser-exclusions-checkbox">
            <h4>Browser Exclusions</h4>
        </Checkbox>
        <template #input>
            <Input v-model="ftBrowserExclusions.value" data-testid="download-modal__browser-exclusions-input" />
        </template>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper
        v-if="displayOptionsForFormat('optimiseTextFiles')"
        tooltip="Minify HTML, CSS and JS files used by the template"
    >
        <Checkbox v-model="optimiseTextFiles" data-testid="download-modal__optimise-files-checkbox">
            <h4>Optimise Files</h4>
        </Checkbox>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper
        v-if="displayOptionsForFormat('optimiseFontFiles')"
        tooltip="Remove unused glyphs from font files used in the template"
    >
        <Checkbox v-model="optimiseFontFiles" data-testid="download-modal__optimise-fonts-checkbox">
            <h4>Optimise Fonts</h4>
        </Checkbox>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper
        v-if="displayOptionsForFormat('optimiseImages')"
        tooltip="This may result in color profile changes"
    >
        <Checkbox v-model="optimiseImages" data-testid="download-modal__optimise-images-checkbox">
            <h4>Optimise Images</h4>
        </Checkbox>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper
        v-if="displayOptionsForFormat('includeBackupImage')"
        tooltip="Include backup images in the download"
    >
        <Checkbox v-model="includeBackupImage" data-testid="download-modal__backup-images-checkbox">
            <h4>Include backup images</h4>
        </Checkbox>
    </DownloadOptionWrapper>
</template>

<script lang="ts" setup>
import { watch } from "vue";
import { DOWNLOAD_FORMAT, type DownloadFormatType } from "hoxton-shared/enums/exports";

import DownloadOptionWrapper from "@/components/Download/DownloadOptionWrapper.vue";
import { useDownloadHTMLZip } from "@/composables/download/useDownloadHTMLZip";

const { downloadFormat } = defineProps<{
    downloadFormat: DownloadFormatType;
}>();

const {
    optimiseFontFiles,
    optimiseTextFiles,
    optimiseImages,
    includeBackupImage,
    ftBrowserExclusions,
    ftClickTagCount,
    dcProfileId,
    dcProfileIdError,

    resetDownloadOptions
} = useDownloadHTMLZip();

watch(
    () => downloadFormat,
    () => {
        resetDownloadOptions();
    }
);

function displayOptionsForFormat(
    option:
        | "optimiseFontFiles"
        | "optimiseTextFiles"
        | "optimiseImages"
        | "includeBackupImage"
        | "ftBrowserExclusions"
        | "ftClickTagCount"
): boolean {
    switch (option) {
        case "optimiseFontFiles":
        case "optimiseTextFiles":
        case "optimiseImages":
            return (
                downloadFormat === DOWNLOAD_FORMAT.GENERIC ||
                downloadFormat === DOWNLOAD_FORMAT.OPENDC ||
                downloadFormat === DOWNLOAD_FORMAT.DOUBLECLICK ||
                downloadFormat === DOWNLOAD_FORMAT.DOUBLECLICK_SV ||
                downloadFormat === DOWNLOAD_FORMAT.FLASHTALKING
            );
        case "includeBackupImage":
            return downloadFormat === DOWNLOAD_FORMAT.GENERIC;
        case "ftBrowserExclusions":
        case "ftClickTagCount":
            return downloadFormat === DOWNLOAD_FORMAT.FLASHTALKING;
        default:
            return false;
    }
}
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;
.tooltip {
    font-size: 12.5px;
}

.error {
    color: $error-color;
    font-size: 10px;
    height: 10px;
}
</style>
